<template>
  <form>
    <b-row>
      <b-col>
        <b-form-group label="Template Name" label-for="name-input">
          <b-form-input
              v-model="formValues.name"
              v-validate="{ required: true }"
              :state="validateState('name-input')"
              aria-describedby="name-input-live-feedback"
              data-vv-name="name-input"
              data-vv-as="Template Name"
              :disabled="this.formValues.id !== null"
              required
          />
          <b-form-invalid-feedback id="gameType-input-live-feedback">{{
              veeErrors.first('name-input')
            }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <Multiselect
            v-model="currentLimit"
            :options="formValues.limits"
            :custom-label="(opt) => this.getOptionLabel(opt)"
            deselect-label=""
            select-label="Select"
            placeholder="Select limit"
        >
        </Multiselect>
      </b-col>
      <b-col class="d-flex align-items-center">
        <button type="button" class="btn btn-sm mr-1 btn-success" v-on:click="addLimit"><i class="fas fa-plus"></i> Add Limit</button>
        <button v-if="currentLimit !== null" type="button" class="btn btn-sm mr-1 btn-danger" v-on:click="removeLimit"><i class="fas fa-trash"></i> Remove Limit</button>
      </b-col>
    </b-row>

    <div v-if="currentLimit !== null" class="mt-3 pr-3 pl-3" style="border-top: 1px solid #ddd">
      <b-row class="mt-3">
        <b-col cols="5">
          <b-form-group label="Table" label-for="table-input">
            <multiselect
                name="table-input"
                v-model="currentLimit.tableId"
                :options="tableOptions"
                deselect-label=""
                select-label="Select"
                placeholder="Select table"
                :options-limit="50"
                :custom-label="(opt) => `${$store.getters['tablesForSelect/getName'](opt)} (${opt})`"
                v-validate="{ required: true }"
                :state="validateState('table-input')"
                aria-describedby="table-input-live-feedback"
                data-vv-name="table-input"
                data-vv-as="Table"
                required
            >
            </multiselect>
            <b-form-invalid-feedback id="table-input-live-feedback" :class="{'d-block' : veeErrors.has('table-input') }">{{
                veeErrors.first('table-input')
              }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col>
          <b-form-group label="Currency" label-for="currency-input">
            <multiselect
                name="currency-input"
                v-model="currentLimit.currency"
                :options="currencyOptions"
                :internal-search="false"
                @search-change="filterCurrency"
                deselect-label=""
                select-label="Select"
                placeholder="Select currency"
                :disabled="currentLimit.tableId === null"
                :options-limit="50"
                v-validate="{ required: true }"
                :state="validateState('currency-input')"
                aria-describedby="currency-input-live-feedback"
                data-vv-name="currency-input"
                data-vv-as="Currency"
                required
            >
            </multiselect>
            <b-form-invalid-feedback id="currency-input-live-feedback" :class="{'d-block' : veeErrors.has('currency-input') }">{{
                veeErrors.first('currency-input')
              }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col>
          <b-form-group label="Limit Type" label-for="type-input">
            <multiselect
                name="type-input"
                v-model="currentLimit.type"
                :options="typeOptions"
                :custom-label="(opt) => `${$store.getters['limits/getTypeName'](opt)} (${opt})`"
                deselect-label=""
                select-label="Select"
                placeholder="Select type"
                :disabled="currentLimit.tableId === null"
                :options-limit="50"
                v-validate="{ required: true }"
                :state="validateState('type-input')"
                aria-describedby="type-input-live-feedback"
                data-vv-name="type-input"
                data-vv-as="Limit Type"
                required
            >
            </multiselect>
            <b-form-invalid-feedback id="type-input-live-feedback" :class="{'d-block' : veeErrors.has('type-input') }">{{
                veeErrors.first('type-input')
              }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-tabs content-class="mt-3" v-if="currentLimit.settings">
        <b-tab :title="prepareLabel(tab)" v-for="tab in regularSettingsTabs(currentLimit.settings)" v-bind:key="tab">
          <b-row v-if="currentLimit.settings[tab] && currentLimit.settings[tab].constructor === Object">
            <b-col cols="6" v-for="key in Object.keys(currentLimit.settings[tab])" v-bind:key="key">
              <b-form-group :label="prepareLabel(key)" :label-for="key + '-input'">
                <b-form-input :id="key + '-input'" v-model="currentLimit.settings[tab][key]"
                              :state="null" autocomplete="off" required :disabled="settingsLoading"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row v-if="Array.isArray(currentLimit.settings[tab])">
            <b-col cols="12" v-for="(chip, index) in currentLimit.settings[tab]" v-bind:key="index">
              <h5>Chip {{ index + 1 }}</h5>
              <b-row>
                <b-col cols="4" v-for="key in Object.keys(chip)" v-bind:key="key"
                       :class="{'d-none': key === 'position'}">
                  <b-form-group :label="prepareLabel(key)" :label-for="key + '-input'">
                    <b-form-input :id="key + '-input'" v-model="currentLimit.settings[tab][index][key]"
                                  :state="null" autocomplete="off" required :disabled="settingsLoading"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-button variant="primary" v-if="Array.isArray(currentLimit.settings[tab])" @click="() => {
              let setting = {...currentLimit.settings[tab][0]};

              Object.keys(setting).forEach(key => {
                setting[key] = '';
              });

              currentLimit.settings[tab].push(setting);
            }">Add
          </b-button>
        </b-tab>
        <b-tab title="Bonus chips">
          <BonusLimitsEditor :settings="currentLimit.settings" :disabled="settingsLoading" />
        </b-tab>
      </b-tabs>
    </div>
    <b-alert :show="!!bonusError" variant="danger" class="mt-3">{{ bonusError }}</b-alert>
  </form>
</template>

<script>
import Multiselect from 'vue-multiselect';
import Vue from "vue";
import BonusLimitsEditor from '../Limits/BonusLimitsEditor.vue';
import { bonusSettingsError, regularSettingsTabs } from '../Limits/bonus-settings';

const prepareLabel = (val) => {
  return val
      .split(/_/g)
      .map((word) => {
        return word[0].toUpperCase() + word.substr(1);
      })
      .join(' ');
};

export default {
  data() {
    return {
      defaultSettings: null,
      formValues: this.getInitialValues(),
      currentLimit: null,
      settingsLoading: false,
      limitSettingsCache: {},
      currencyQuery: null,
      bonusError: ''
    }
  },
  components: {
    Multiselect,
    BonusLimitsEditor,
  },
  props: {
  },
  methods: {
    regularSettingsTabs,
    setData(data) {
      this.setDefaults();
      for (let field in data) {
        Vue.set(this.formValues, field, data[field]);
      }

      this.formValues.limits.forEach(limit => {
        this.limitSettingsCache[limit.tableId + '_' + limit.currency + '_' + limit.type] = limit.settings;
      })

    },
    getData() {
        return this.formValues;
    },
    isValid() {
      this.bonusError = this.formValues.limits.map(limit => bonusSettingsError(limit.settings)).find(Boolean) || '';
      if (this.bonusError) return Promise.resolve(false);
      return this.$validator.validateAll();
    },
    filterCurrency(query) {
      this.currencyQuery = query
    },
    getOptionLabel(item) {
      let tableName = !item.tableId ? 'N/A' : `${this.$store.getters['tablesForSelect/getName'](item.tableId)} (${item.tableId})`;
      let currencyName = !item.currency ? 'N/A' : item.currency

      return `${tableName} - ${currencyName}`;
    },
    addLimit() {
      let newLimit = {
        tableId: null,
        type: null,
        currency: null,
        settings: null
      }
      this.formValues.limits.push(newLimit)
      this.currentLimit = newLimit
    },
    removeLimit() {
      if (!this.currentLimit) {
        return
      }

      let index = this.formValues.limits.indexOf(this.currentLimit)
      this.formValues.limits.splice(index, 1)
      this.currentLimit = null
    },
    getInitialValues() {
      return {
        id: null,
        name: null,
        limits: [],
      };
    },
    loadDefaultSettings(gameType) {
      this.settingsLoading = true;

      this.$store
          .dispatch('limits/loadDefaultSettings', gameType)
          .then(() => {
            this.settingsLoading = false;
            this.currentLimit.settings = this.$store.getters['limits/defaultSettings'](gameType)?.settings ?? null;
          })
          .catch(err => {
            console.log(err);
          });
    },
    prepareLabel: prepareLabel,
    validateState(ref) {
      if (
          this.veeFields[ref] &&
          (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.veeErrors.has(ref);
      }
      return null;
    },
    setDefaults() {
      let initialValues = this.getInitialValues();

      for (let field in initialValues) {
        Vue.set(this.formValues, field, initialValues[field]);
      }
    },
  },
  computed: {
    typeOptions() {
      return this.$store.getters['limits/typesForSelect']?.map(option => {
        return option.value;
      }) || [];
    },
    tableOptions() {
      return this.$store.getters['tablesForSelect/list'].map(opt => { return Number.parseInt(opt.value)})
    },
    currencyOptions() {
      if (!this.currentLimit) {
        return [];
      }

      if (!this.currentLimit.tableId) {
        return [];
      }

      let usedCurrencies = [];
      this.formValues.limits.forEach((limit) => {
        if (limit === this.currentLimit || limit.tableId !== this.currentLimit.tableId) {
          return
        }

        usedCurrencies.push(limit.currency);
      })

      return this.$store.getters['limits/currenciesForSelect']
          ?.filter(option => {
            if (!this.currencyQuery) {
              return !usedCurrencies.includes(option.value)
            }

            return !usedCurrencies.includes(option.value) && option.value.match(new RegExp("^" + this.currencyQuery.replace(/\W/g, "\\$&") + ".*", 'i'))
          })
          .map(option => {
            return option.value;
          })
          .sort() || [];
    },
  },
  watch: {
    'currentLimit.type'(newType, oldType) {
      let valueFromCache = null;
      if (this.currentLimit) {
        this.limitSettingsCache[this.currentLimit.tableId + '_' + this.currentLimit.currency + '_' + oldType] = this.currentLimit.settings;
        valueFromCache = this.limitSettingsCache[this.currentLimit.tableId + '_' + this.currentLimit.currency + '_' + newType];
      }

      if (!valueFromCache) {
        this.loadDefaultSettings(newType);
      } else if (this.currentLimit) {
        this.currentLimit.settings = valueFromCache
      }

      if (!newType && this.currentLimit) {
        this.currentLimit.currency = null
      }
    },
    'currentLimit.tableId'(newTableId) {
        let table = this.$store.getters['tablesForSelect/getTable'](newTableId);
        if (!table) {
          return;
        }

        let type = this.$store.getters['limits/typesForSelect'].find(opt => opt.value == table.gameType);
        if (!type) {
          return
        }

        this.currentLimit.type = type.value
    },
  },
  mounted() {
    this.setDefaults();
    this.$store.dispatch('tablesForSelect/loadList')
    this.$store.dispatch('limits/ensureLoadTypesForSelect');
    this.$store.dispatch('limits/ensureLoadCurrenciesForSelect');
  },
}
</script>
